import { Intl } from '@erecruit/core';
import { UserNotificationType } from '@erecruit/types';
import { Icons, Status } from '@erecruit/ui-kit';
import {
  BackgroundNotification,
  isMatchPayload,
  isPositionPayload,
  NotificationContext,
} from '@erecruit/modules';

import { AppNotification } from './types';

const localizedNotificationTypes = messageIds.vms.notifications.types;

export const PAGE_SIZE = 10;

export const notificationTypesLocalizations: Record<
  number,
  Intl.LocalizationKeysArray
> = {
  [UserNotificationType.PositionApprovalStarted]:
    localizedNotificationTypes.positionApprovalStarted,
  [UserNotificationType.MatchApprovalStarted]:
    localizedNotificationTypes.matchApprovalStarted,
  [UserNotificationType.PositionApprovalPending]:
    localizedNotificationTypes.positionApprovalPending,
  [UserNotificationType.MatchApprovalPending]:
    localizedNotificationTypes.matchApprovalPending,
  [UserNotificationType.PositionApprovalCompleted]:
    localizedNotificationTypes.positionApprovalCompleted,
  [UserNotificationType.MatchApprovalCompleted]:
    localizedNotificationTypes.matchApprovalCompleted,
  [UserNotificationType.PositionApprovalRejected]:
    localizedNotificationTypes.positionApprovalRejected,
  [UserNotificationType.MatchApprovalRejected]:
    localizedNotificationTypes.matchApprovalRejected,
  [UserNotificationType.CandidateSubmitted]:
    localizedNotificationTypes.candidateSubmitted,
  [UserNotificationType.CandidateAcceptedOffer]:
    localizedNotificationTypes.candidateAcceptedOffer,
  //below are new notifications which weren't present before
  [UserNotificationType.PositionApprovalNeedsApprovers]:
    localizedNotificationTypes.positionApprovalNeedsApprovers,
  [UserNotificationType.MatchApprovalNeedsApprovers]:
    localizedNotificationTypes.matchApprovalNeedsApprovers,
};

export const notificationTypeVisualLookup: Record<
  number,
  { color: Status; icon: Icons }
> = {
  [UserNotificationType.PositionApprovalStarted]: {
    color: 'pending',
    icon: 'time',
  },
  [UserNotificationType.MatchApprovalStarted]: {
    color: 'pending',
    icon: 'time',
  },
  [UserNotificationType.PositionApprovalPending]: {
    color: 'pending',
    icon: 'approvals',
  },
  [UserNotificationType.MatchApprovalPending]: {
    color: 'pending',
    icon: 'approvals',
  },
  [UserNotificationType.PositionApprovalCompleted]: {
    color: 'success',
    icon: 'done-all',
  },
  [UserNotificationType.MatchApprovalCompleted]: {
    color: 'success',
    icon: 'done-all',
  },
  [UserNotificationType.PositionApprovalRejected]: {
    color: 'critical',
    icon: 'close',
  },
  [UserNotificationType.MatchApprovalRejected]: {
    color: 'critical',
    icon: 'close',
  },
  [UserNotificationType.CandidateSubmitted]: {
    color: 'success',
    icon: 'done-all',
  },
  [UserNotificationType.CandidateAcceptedOffer]: {
    color: 'success',
    icon: 'done-all',
  },
  //below are new notifications which weren't present before
  [UserNotificationType.PositionApprovalNeedsApprovers]: {
    color: 'success',
    icon: 'done-all',
  },
  [UserNotificationType.MatchApprovalNeedsApprovers]: {
    color: 'success',
    icon: 'done-all',
  },
};

export const getNotificationLink = (context?: NotificationContext) => {
  if (!context) return undefined;
  let path = '';

  if (isMatchPayload(context)) {
    path = `/match/`;
  } else if (isPositionPayload(context)) {
    path = `/position/`;
  }

  path = `${path}${context.id}`;
  return path;
};

export const createNotification = (
  { data: { body, id, data } }: BackgroundNotification,
  intl: Intl.InjectedIntl,
): AppNotification => {
  const parsedContexts = JSON.parse(data.context) as NotificationContext[];
  const type = Number(data.notificationType);
  const notificationLink = getNotificationLink(parsedContexts[0]);

  return {
    id,
    body,
    title: intl.formatMessage({
      id: notificationTypesLocalizations[type],
    }),
    createdOn: new Date().toISOString(),
    type,
    link: notificationLink
      ? {
          to: notificationLink,
          text: intl.formatMessage({ id: messageIds.vms.notifications.link }),
        }
      : undefined,
    data: data,
    read: false,
  };
};
